import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ImageUploader from "../components/ImageUploader";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Check } from "lucide-react";
import { toast } from "sonner";

const TRI = ["available", "unavailable", "nc"];

export default function PostListing() {
  const { user, loading } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [tree, setTree] = useState({});
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", price_da: 0, price_on_request: false,
    transaction: "sale", type: "apartment",
    wilaya: "Alger", city: "", neighborhood: "", surface: 0, rooms: 0, bathrooms: 0,
    furnished: false, condition: "old", balcony: false, parking: false,
    swimming_pool: false, garden: false, air_conditioning: false, heating: false, elevator: false,
    floor: null, orientation: null, payment_type: "cash",
    title_deed: "nc", land_registry: "nc", negative_cert: "nc",
    images: [],
    phone: user?.phone || "", whatsapp: user?.phone || "",
  });

  useEffect(() => { if (!loading && !user) navigate("/login"); }, [user, loading, navigate]);
  useEffect(() => { api.get("/locations").then((r) => setTree(r.data.tree)); }, []);
  const cities = tree[form.wilaya] || [];

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setNum = (k, v) => set(k, parseInt(v || 0, 10));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.phone || !form.city || !form.surface) {
      toast.error("Champs requis manquants : titre, ville, surface ou téléphone");
      return;
    }
    setBusy(true);
    try {
      // Clean payload: convert "" to null for optional fields
      const payload = { ...form };
      if (payload.floor === "" || payload.floor === undefined) payload.floor = null;
      if (payload.orientation === "" || payload.orientation === "_") payload.orientation = null;
      // Ensure numerics
      payload.price_da = parseInt(payload.price_da || 0, 10);
      payload.surface = parseInt(payload.surface || 0, 10);
      payload.rooms = parseInt(payload.rooms || 0, 10);
      payload.bathrooms = parseInt(payload.bathrooms || 0, 10);

      const { data } = await api.post("/properties", payload);
      toast.success("Annonce publiée !");
      navigate(`/property/${data.id}`);
    } catch (err) {
      // Pydantic returns array of errors → flatten to readable string
      const detail = err.response?.data?.detail;
      let msg = "Erreur lors de la publication";
      if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail) && detail.length > 0) {
        msg = detail.map((d) => `${d.loc?.slice(-1)[0] || ""}: ${d.msg}`).join(" · ");
      }
      toast.error(msg);
      console.error("Listing creation failed:", err.response?.data || err);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <div className="pt-28 pb-20 max-w-3xl mx-auto px-5">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-[#111827]">{t("quickListing")}</h1>
          <p className="text-slate-500 mt-2">{t("quickSub")}</p>
        </div>

        <form onSubmit={submit} className="space-y-6" data-testid="post-form">
          <Card title={t("photos")}>
            <ImageUploader value={form.images} onChange={(imgs) => set("images", imgs)} />
          </Card>

          <Card title={t("basicInfo")}>
            <div className="space-y-4">
              <F label={t("title")}>
                <Input required value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="post-title" placeholder="Ex: Appartement F3 lumineux Hydra" />
              </F>
              <F label={t("description")}>
                <Textarea rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="post-desc" />
              </F>

              <div className="grid grid-cols-2 gap-4">
                <F label={t("transaction")}>
                  <div className="flex rounded-xl bg-slate-50 p-1">
                    {["sale", "rent"].map((v) => (
                      <button key={v} type="button" onClick={() => set("transaction", v)} className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold ${form.transaction === v ? "bg-white text-[#1B8F5A] shadow-sm" : "text-slate-500"}`}>{v === "sale" ? t("sale") : t("rentLabel")}</button>
                    ))}
                  </div>
                </F>
                <F label={t("propertyType")}>
                  <Select value={form.type} onValueChange={(v) => set("type", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="apartment">{t("apartment")}</SelectItem>
                      <SelectItem value="house">{t("house")}</SelectItem>
                      <SelectItem value="land">{t("land")}</SelectItem>
                    </SelectContent>
                  </Select>
                </F>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <F label={t("surface") + " m²"}><Input type="number" required value={form.surface || ""} onChange={(e) => setNum("surface", e.target.value)} /></F>
                <F label={t("rooms")}><Input type="number" value={form.rooms || ""} onChange={(e) => setNum("rooms", e.target.value)} /></F>
                <F label={t("bathrooms")}><Input type="number" value={form.bathrooms || ""} onChange={(e) => setNum("bathrooms", e.target.value)} /></F>
              </div>

              <F label={t("condition")}>
                <div className="flex gap-2">
                  {[["new", t("new")], ["old", t("old")]].map(([v, l]) => (
                    <button key={v} type="button" onClick={() => set("condition", v)} className={`px-4 py-2 rounded-lg text-sm font-semibold border ${form.condition === v ? "bg-[#1B8F5A] text-white border-[#1B8F5A]" : "bg-white border-slate-200"}`}>{l}</button>
                  ))}
                </div>
              </F>

              <F label={t("features")}>
                <div className="grid grid-cols-2 gap-2">
                  {[["furnished", t("furnished")], ["balcony", t("balcony")], ["parking", t("parking")], ["swimming_pool", t("swimmingPool")], ["garden", t("garden")], ["air_conditioning", t("airConditioning")], ["heating", t("heating")], ["elevator", t("elevator")]].map(([k, l]) => (
                    <label key={k} className="flex items-center gap-2 p-2.5 border border-slate-200 rounded-lg cursor-pointer hover:border-[#1B8F5A]">
                      <Checkbox checked={!!form[k]} onCheckedChange={(v) => set(k, !!v)} data-testid={`post-${k.replace("_","-")}`} />
                      <span className="text-sm">{l}</span>
                    </label>
                  ))}
                </div>
              </F>
              <div className="grid grid-cols-2 gap-3">
                <F label={t("floor")}>
                  <Input type="number" value={form.floor ?? ""} onChange={(e) => set("floor", e.target.value === "" ? null : parseInt(e.target.value, 10))} placeholder="0 = RDC" data-testid="post-floor" />
                </F>
                <F label={t("orientation")}>
                  <Select value={form.orientation || "_"} onValueChange={(v) => set("orientation", v === "_" ? null : v)}>
                    <SelectTrigger data-testid="post-orientation"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_">—</SelectItem>
                      {["N", "S", "E", "W", "NE", "NW", "SE", "SW"].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </F>
              </div>
            </div>
          </Card>

          <Card title={t("priceLocation")}>
            <div className="space-y-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={form.price_on_request} onCheckedChange={(v) => set("price_on_request", !!v)} data-testid="post-por" />
                <span className="text-sm font-medium">{t("priceOnRequest")}</span>
              </label>
              {!form.price_on_request && (
                <div className="grid grid-cols-2 gap-4">
                  <F label={t("price")}><Input type="number" required={!form.price_on_request} value={form.price_da || ""} onChange={(e) => setNum("price_da", e.target.value)} data-testid="post-price" /></F>
                  <F label={t("paymentType")}>
                    <Select value={form.payment_type} onValueChange={(v) => set("payment_type", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">{t("cash")}</SelectItem>
                        <SelectItem value="installment">{t("installment")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </F>
                </div>
              )}
              <div className="grid grid-cols-3 gap-3">
                <F label={t("location")}>
                  <Select value={form.wilaya} onValueChange={(v) => setForm({ ...form, wilaya: v, city: "" })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.keys(tree).map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}</SelectContent>
                  </Select>
                </F>
                <F label={t("city")}>
                  <Select value={form.city || ""} onValueChange={(v) => set("city", v)}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </F>
                <F label={t("neighborhood")}><Input value={form.neighborhood} onChange={(e) => set("neighborhood", e.target.value)} /></F>
              </div>
            </div>
          </Card>

          <Card title={t("documents")}>
            <div className="space-y-3">
              {[["title_deed", t("titleDeed")], ["land_registry", t("landRegistry")], ["negative_cert", t("negativeCert")]].map(([k, l]) => (
                <div key={k} className="flex items-center justify-between gap-3 p-3 bg-slate-50 rounded-xl">
                  <span className="text-sm font-medium">{l}</span>
                  <div className="flex gap-1.5">
                    {TRI.map((v) => (
                      <button key={v} type="button" onClick={() => set(k, v)} className={`px-3 py-1.5 rounded-md text-xs font-semibold ${form[k] === v ? "bg-[#1B8F5A] text-white" : "bg-white text-slate-600 border border-slate-200"}`}>
                        {v === "available" ? "✓" : v === "unavailable" ? "✗" : "NC"}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card title={t("contactOwner")}>
            <div className="grid grid-cols-2 gap-4">
              <F label={t("phone")}><Input required value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+213..." /></F>
              <F label="WhatsApp"><Input value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="+213..." /></F>
            </div>
          </Card>

          <Button type="submit" disabled={busy} className="w-full bg-[#1B8F5A] hover:bg-[#157047] h-14 text-base font-semibold disabled:opacity-60" data-testid="post-submit">
            {busy ? (
              <span className="flex items-center gap-2"><span className="inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Publication…</span>
            ) : t("publish")}
          </Button>
        </form>
      </div>
      <Footer />
    </div>
  );
}

function Card({ title, children }) {
  return <div className="bg-white p-6 rounded-2xl border border-slate-200"><h2 className="text-base font-bold text-[#111827] mb-4">{title}</h2>{children}</div>;
}
function F({ label, children }) {
  return <div><label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">{label}</label>{children}</div>;
}
